/* __next_internal_client_entry_do_not_use__ Default auto */ import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
// Global
import { useEffect, useMemo } from 'react';
import { tv } from 'tailwind-variants';
import { withStandardComponentWrapper } from 'helpers/HOC';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import { getTestProps } from 'lib/testing/utils';
import useIsEditing from 'lib/hooks/useIsEditing';
import { useSetIsDataAvailable } from 'lib/context/IsDataAvailableContext';
import { EditingHelpText } from 'helpers/Editing/EditingHelpText';
import useDictionary from 'lib/hooks/useDictionary';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';
const resolveMultilistItems = (field)=>{
    if (!field) return [];
    if (Array.isArray(field)) return field;
    if (Array.isArray(field.jsonValue)) return field.jsonValue;
    if (Array.isArray(field.targetItems)) return field.targetItems;
    return [];
};
const getLinkField = (item)=>{
    const link = item?.fields?.link;
    if (!link) return undefined;
    if ('value' in link && link.value) return link;
    return {
        value: link
    };
};
const isValidLinkItem = (item)=>{
    const linkValue = getLinkField(item)?.value;
    const href = linkValue?.href?.trim();
    const text = linkValue?.text?.trim();
    return Boolean(href && text);
};
const LinkListBlock = (props)=>{
    const { getDictionaryValue } = useDictionary();
    const isEditing = useIsEditing();
    const setIsDataAvailable = useSetIsDataAvailable();
    const { RenderingIdentifier, smallContainer } = props?.params || {};
    // Width: `contentWrapper` is always on the list so Layout's `.small-container`
    // (expert profiles) constrains it — same as NewsList / ProfileAccordion.
    // Columns/centering: use constrained layout when Small Container is checked, or when
    // the param is absent (template not synced yet). Explicit unchecked (`''`) keeps full layout.
    // Note: `smallContainer` is a string coming from Sitecore params.
    const isSmallContainer = smallContainer === '1';
    const links = useMemo(()=>resolveMultilistItems(props?.fields?.links), [
        props?.fields?.links
    ]);
    const linkItems = useMemo(()=>links.filter(isValidLinkItem), [
        links
    ]);
    const linkItemCount = linkItems.length;
    const shouldCenterAlign = isSmallContainer ? linkItemCount === 1 : linkItemCount <= 2;
    const { listWrapper, listItem, linkLine, linkListWrapper } = TAILWIND_VARIANTS({
        isSmallContainer,
        shouldCenterAlign
    });
    useEffect(()=>{
        const available = !isEditing && linkItemCount > 0;
        setIsDataAvailable(available);
    }, [
        linkItemCount,
        isEditing,
        setIsDataAvailable
    ]);
    if (isEditing && linkItemCount === 0) {
        return /*#__PURE__*/ _jsx("section", {
            "data-component": "authorable/shared/lists/linklistblock",
            ...editingHydrationProps(isEditing),
            ...getTestProps(`component-link-list-block-${props?.rendering?.uid}`),
            children: /*#__PURE__*/ _jsx(EditingHelpText, {
                priority: "warning",
                children: getDictionaryValue('LinkListBlockHelpText') || 'Please select links in the multilist field on this component datasource to display the link list.'
            })
        });
    }
    if (linkItemCount === 0) {
        return /*#__PURE__*/ _jsx(_Fragment, {});
    }
    return /*#__PURE__*/ _jsx("section", {
        className: linkListWrapper(),
        "data-component": "authorable/shared/lists/linklistblock",
        ...editingHydrationProps(isEditing),
        id: RenderingIdentifier,
        ...getTestProps(`component-link-list-block-${props?.rendering?.uid}`),
        children: /*#__PURE__*/ _jsx("ul", {
            className: listWrapper(),
            children: linkItems.map((item, index)=>{
                const linkField = getLinkField(item);
                if (!linkField) return null;
                return /*#__PURE__*/ _jsx("li", {
                    className: listItem(),
                    id: item.id,
                    children: /*#__PURE__*/ _jsx(LinkWrapper, {
                        ctaComponentClass: "component-card-item-button-1-color",
                        className: linkLine(),
                        ctaVariant: "tertiary",
                        field: linkField,
                        ...getTestProps(`link-item-${index}`),
                        suppressNewTabIcon: true,
                        ctaIcon: "arrow-dash-right"
                    })
                }, item.id || `link-${index}`);
            })
        })
    });
};
export const Default = withStandardComponentWrapper(LinkListBlock, false);
const TAILWIND_VARIANTS = tv({
    slots: {
        // Always include `contentWrapper` so Layout's `.small-container` rule in app.css
        // constrains width on expert profile pages (does nothing when parent lacks that class).
        listWrapper: [
            'flex',
            'flex-wrap',
            'list-none',
            'p-0',
            'gap-x-layout-base-margin-x',
            'gap-y-layout-base-gutter'
        ],
        listItem: [
            'shrink-0',
            'flex',
            'w-full',
            'md:w-[calc((100%-var(--layout-base-margin-x))/2)]',
            'py-spacing-spacing-1',
            'text-component-Profile-Block-list-item',
            'font-typography-font-family-body',
            'text-typography-font-size-body-medium',
            'leading-typography-line-height-body-medium'
        ],
        linkLine: [
            '!font-normal',
            'justify-between',
            'grow',
            '!text-Text-text-default',
            'text-left'
        ],
        linkListWrapper: []
    },
    variants: {
        isSmallContainer: {
            true: {
                listWrapper: [
                    'contentWrapper'
                ],
                listItem: [
                    'lg:w-[calc((100%-var(--layout-base-margin-x))/2)]'
                ],
                linkListWrapper: [
                    'small-container'
                ]
            },
            false: {
                listItem: [
                    'lg:w-[calc((100%-2*var(--layout-base-margin-x))/3)]'
                ],
                linkListWrapper: []
            }
        },
        shouldCenterAlign: {
            true: {
                listWrapper: [
                    'justify-center'
                ],
                listItem: [
                    'justify-center'
                ]
            },
            false: {
                listWrapper: [
                    'justify-start'
                ],
                listItem: [
                    'justify-start'
                ]
            }
        }
    },
    defaultVariants: {
        isSmallContainer: false,
        shouldCenterAlign: false
    }
});
